import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { z } from "zod";
import useApi from "../../../shared/hooks/useApi";
import useControlledForm from "../../../shared/hooks/useControlledForm";
import { PatientId, PatientTaskDocumentChangeRequestId } from "../../../shared/schema/schema";
import { createDisclosureTriggerComponent } from "../../../shared/utils/create-disclosure-trigger-component";

type Props = {
  patientId: PatientId;
  changeRequestId: PatientTaskDocumentChangeRequestId;
  children: React.ReactNode;
  disclosure: ReturnType<typeof useDisclosure>;
  onResolve: () => void;
};

const zResolveChangeRequest = z.object({
  message: z.string().optional(),
});

function ResolveModal(props: Props) {
  const { api } = useApi();
  const toast = useToast();

  const handleClose = () => {
    props.disclosure.onClose();
    form.reset();
  };

  const form = useControlledForm({
    schema: zResolveChangeRequest,
    onSuccess: (r) => submitRequest.mutate(r.message),
  });

  const submitRequest = useMutation({
    mutationFn: (message?: string) =>
      api.post("./patients/:patientId/task_change_requests/:changeRequestId/resolve", {
        path: { patientId: props.patientId, changeRequestId: props.changeRequestId },
        body: { message: message === undefined || message === "" ? null : message },
      }),
    onSuccess: () => {
      toast({
        position: "top-right",
        title: "Change request resolved",
        status: "success",
      });
      handleClose();
      props.onResolve();
    },
    onError: (error) => {
      toast({
        position: "top-right",
        title: "Failed to resolve change request",
        description: error.message,
        status: "error",
      });
    },
  });

  return (
    <Modal closeOnOverlayClick={false} size="md" {...props.disclosure} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={form.onSubmit}>
          <ModalHeader>Resolve Change Request</ModalHeader>
          <ModalBody>
            <FormControl isInvalid={form.isInvalid("message")}>
              <FormLabel>Message:</FormLabel>
              <Textarea
                placeholder="Describe the resolution of the change request (optional)"
                value={form.state.message}
                onChange={(e) => form.setValue("message", e.target.value)}
              />
              <FormErrorMessage>{form.getError("message")}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button disabled={submitRequest.isPending} mr="2" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" isLoading={submitRequest.isPending} type="submit">
              Submit
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export const ChangeRequestResolveModal = createDisclosureTriggerComponent<
  Omit<Props, "disclosure">
>({
  displayName: "ChangeRequestResolveModalTrigger",
  render: (props) => {
    return <ResolveModal {...props} />;
  },
});
